import { InlineAlertType } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack } from 'expo-router';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import { OtpVerificationType } from 'features/otp/constants/OtpVerificationType';
import ResendOtpButton from 'features/otp/ResendOtpButton/ResendOtpButton';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Linking } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isAndroid, isIOS, isWeb } from 'shared/constants/platform';
import KeyboardAvoidingContainer from 'shared/containers/KeyboardAvoidingContainer/KeyboardAvoidingContainer';

export interface EmailVerificationScreenProps {
  // Text content
  headerText: string;
  titleText: string;

  // Optional copy customization
  openMailButtonLabel?: string;
  newLinkSentText: string;
  genericErrorText: string;

  // Interpolated items
  description;

  // Flow context
  flowKey?: OtpFlowKey;

  // Event handlers
  onResendLink: () => Promise<{
    errorCode?: number;
    title?: string;
    message?: string;
  }>;
  onBack: () => Promise<void> | void;
  onOpenMail?: () => void;
}

function EmailVerificationScreen({
  headerText,
  titleText,
  openMailButtonLabel,
  newLinkSentText,
  genericErrorText,
  description,
  flowKey = OtpFlowKey.PROFILE_CHANGE,
  onResendLink,
  onBack,
  onOpenMail,
}: EmailVerificationScreenProps) {
  const [screenError, setScreenError] = useState<{
    title?: string;
    message?: string;
  } | null>(null);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [resendAttempt, setResendAttempt] = useState(1);

  const { t: translatedText } = useTranslation('auth', {
    keyPrefix: 'shared.checkEmail',
  });

  // Handle resend email link
  const handleResendLink = async () => {
    setResendAttempt(resendAttempt + 1);
    setSnackbarMessage(null);
    setScreenError(null);
    setIsResendLoading(true);

    try {
      const { errorCode, message, title } = await onResendLink();

      if (errorCode) {
        setScreenError({ title, message });
        return;
      }

      setSnackbarMessage(newLinkSentText);
    } catch {
      setScreenError({ message: genericErrorText });
    } finally {
      setIsResendLoading(false);
    }
  };

  const openMailApp = async () => {
    if (isIOS) {
      const supported = await Linking.canOpenURL('message:');
      if (supported) {
        await Linking.openURL('message:');
      } else {
        Alert.alert(
          translatedText('alert.ios.title'),
          translatedText('alert.ios.message'),
        );
      }
    }
    if (isAndroid) {
      const gmailUrl = 'https://gmail.app.goo.gl';
      const supported = await Linking.canOpenURL(gmailUrl);
      if (supported) {
        await Linking.openURL(gmailUrl);
      } else {
        Alert.alert(
          translatedText('alert.android.title'),
          translatedText('alert.android.message'),
        );
      }
    }
    if (isWeb) {
      const mailAppUrl = 'mailto:';
      const supported = await Linking.canOpenURL(mailAppUrl);
      if (supported) {
        await Linking.openURL(mailAppUrl);
      } else {
        Alert.alert(
          translatedText('alert.android.title'),
          translatedText('alert.android.message'),
        );
      }
    }
  };

  return (
    <KeyboardAvoidingContainer>
      <Stack.Screen
        options={{
          headerBackVisible: true,
          headerShown: true,
          title: titleText,
          header: (props: NativeStackHeaderProps) => (
            <NavigationBar
              {...props}
              title={titleText}
              primaryOnPress={onBack}
            />
          ),
        }}
      />

      {snackbarMessage ? (
        <Snackbar
          text={snackbarMessage}
          buttonOnPress={() => setSnackbarMessage(null)}
        />
      ) : null}

      <KeyboardAvoidingContainer.Content>
        {screenError ? (
          <InlineAlert
            alertType={InlineAlertType.Error}
            titleText={screenError.title || undefined}
            bodyText={screenError.message}
          />
        ) : null}
        <TitleLgText numberOfLines={0}>{headerText}</TitleLgText>
        <BodyBaseText numberOfLines={0}>{description}</BodyBaseText>
        <ResendOtpButton
          onPress={handleResendLink}
          isLoading={isResendLoading}
          isDisabled={false}
          resendCodeAttempt={resendAttempt}
          initialCountdown={20}
          flowKey={flowKey}
          verificationType={OtpVerificationType.EMAIL}
        />
      </KeyboardAvoidingContainer.Content>

      <KeyboardAvoidingContainer.Actions>
        {openMailButtonLabel ? (
          <Button
            label={openMailButtonLabel}
            onPress={() => {
              void openMailApp();
              onOpenMail?.();
            }}
            size="LARGE"
            type="PRIMARY"
            width="FULL"
          />
        ) : null}
      </KeyboardAvoidingContainer.Actions>
    </KeyboardAvoidingContainer>
  );
}

export default EmailVerificationScreen;
